import React from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Action } from "typescript-fsa";
import { FormattedMessage } from "react-intl";

import { makePlaylistTrendsGet } from "@api/index";
import { ApiButton } from "@api/components/ApiButton";
import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { ApiRequest } from "@api/requests";
import { AuthState } from "@auth/models";
import { FilesState } from "@files/models";
import { MusicState } from "@music/models";
import { VendorState } from "@vendor/models";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import { messages as uiMessages } from "@ui/messages";
import {
    AlbumTrackVendorProps,
    makeGetPlaylistTrends,
    getPlaylistTrendsLastLoadedForProps,
    playlistTrendsGetNextPage,
    playlistTrendsHasMorePages,
    getSettingFrom,
    getSettingTo,
    PlaylistSetPair,
} from "../../selectors";
import { PlaylistTrendsTable } from "../PlaylistTrendsTable";
import { SalesState } from "../../models";

interface PlaylistTrendsListOwnProps extends AlbumTrackVendorProps {
    download?: boolean;
    showTracks?: boolean;
}

interface PlaylistTrendsListStateProps extends ApiConnectedOwnProps {
    data: PlaylistSetPair[];
    downloadAction: () => Action<ApiRequest>;
    morePages: boolean;
    nextPage: number;
    nextPageActionCreator: (page?: number) => Action<ApiRequest>;
}

type State = SalesState & VendorState & MusicState & FilesState & AuthState;

type Props = PlaylistTrendsListOwnProps & PlaylistTrendsListStateProps;

const PlaylistTrendsList = ({
    data,
    download,
    showTracks,
    downloadAction,
    morePages,
    nextPage,
    nextPageActionCreator,
}: Props) => {
    if (download == null) {
        download = true;
    }
    return (
        <div className="playlist-trends-list">
            <PlaylistTrendsTable data={data} showTracks={showTracks} />
            {morePages &&
                <div className="playlist-trends-list__load-more">
                    <ApiButton
                        color="primary"
                        actionCreators={[() => nextPageActionCreator(nextPage)]}
                    >
                        <FormattedMessage {...uiMessages.loadMore} />
                    </ApiButton>
                </div>
            }
            {download && <DownloadCsvButton actionCreator={downloadAction} />}
        </div>
    );
};

const makeMapStateToProps = () => {
    const getPlaylistTrends = makeGetPlaylistTrends();
    return (state: State, props: AlbumTrackVendorProps) => {
        const from = getSettingFrom(state);
        const to = getSettingTo(state);
        const playlistTrendsGet = (page?: number) =>
            makePlaylistTrendsGet(props.albumId, props.trackId, props.artistId, from, to, false, page)();
        const actionCreators = [ playlistTrendsGet ];
        const requestId = [ from.format() + "_" + to.format() ];
        const lastLoaded = [getPlaylistTrendsLastLoadedForProps(state, props)];
        return {
            data: getPlaylistTrends(state, props),
            downloadAction: makePlaylistTrendsGet(props.albumId, props.trackId, props.artistId, from, to, true),
            morePages: playlistTrendsHasMorePages(state, props),
            nextPage: playlistTrendsGetNextPage(state, props),
            nextPageActionCreator: playlistTrendsGet,
            // connectToApi options
            actionCreators,
            requestId,
            lastLoaded,
        } as PlaylistTrendsListStateProps;
    };
};

const ConnectedPlaylistTrendsList = compose<Props, PlaylistTrendsListOwnProps>(
    connect(makeMapStateToProps, null),
    connectToApi({}),
)(PlaylistTrendsList);

export {
    PlaylistTrendsList as PlaylistTrendsListPresenter,
    ConnectedPlaylistTrendsList as PlaylistTrendsList,
};
